import { tv } from 'tailwind-variants';

/**
 * Outer container chrome for the CodeEditor.
 *
 * Uses standard Tailwind utility classes that resolve through the existing
 * design-tokens (bg-default, text-default, border-default, etc). CodeMirror's
 * EditorView.theme inside CodeEditor.theme.ts uses `inherit` so the editor
 * body picks up these colours from the container.
 *
 * Light/dark mode is automatic via the design-tokens layer — this file does
 * not need explicit `dark:` variants for body colours.
 */
export const codeEditorVariants = tv({
  base: [
    'border border-default bg-default text-default rounded-lg overflow-hidden',
    'focus-within:border-status-selected focus-within:outline-none',
    // Syntax highlighting palette as CSS custom properties — light defaults
    // here, `dark:` variants below. The EditorView theme in CodeEditor.theme.ts
    // references these vars, so syntax colors automatically switch with the
    // app's `.dark` mode (Tailwind `darkMode: 'selector'`).
    '[--cm-syntax-comment:#6e7781]',
    '[--cm-syntax-keyword:#cf222e]',
    '[--cm-syntax-string:#0a3069]',
    '[--cm-syntax-number:#0550ae]',
    '[--cm-syntax-property:#953800]',
    '[--cm-syntax-punctuation:#57606a]',
    'dark:[--cm-syntax-comment:#8b949e]',
    'dark:[--cm-syntax-keyword:#ff7b72]',
    'dark:[--cm-syntax-string:#a5d6ff]',
    'dark:[--cm-syntax-number:#79c0ff]',
    'dark:[--cm-syntax-property:#ffa657]',
    'dark:[--cm-syntax-punctuation:#8b949e]',
    // Gutter + active-line palette. Same approach as --cm-syntax-* —
    // referenced from CodeEditor.theme.ts via var(...). Defaults are light
    // mode; `dark:` variants kick in under the app's `.dark` selector.
    '[--cm-gutter-fg:#6e7781]',
    '[--cm-gutter-border:#d0d7de]',
    '[--cm-active-line-bg:#f6f8fa]',
    '[--cm-active-line-gutter-bg:#eaeef2]',
    'dark:[--cm-gutter-fg:#8b949e]',
    'dark:[--cm-gutter-border:#30363d]',
    'dark:[--cm-active-line-bg:#161b22]',
    'dark:[--cm-active-line-gutter-bg:#1c2128]',
  ],
  variants: {
    size: {
      small: 'text-xs',
      default: 'text-sm',
      large: 'text-base',
    },
    hasError: {
      true: 'border-status-error',
      false: '',
    },
    disabled: {
      true: 'bg-status-disabled text-status-disabled-dark pointer-events-none',
      false: '',
    },
  },
  defaultVariants: {
    size: 'default',
    hasError: false,
    disabled: false,
  },
});
